import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import personVedio from '../../assets/PURNENDU SHEKHAR.mov';
import personvedio2 from '../../assets/Col.Lakshyajeet.mp4';

const Testimonials = [
  {
    name: 'Col. Lakshyajeet Singh Chauhan',
    designation: "Distinguished Army Officer, Pioneer of India's Indigenous Defence Drone Initiative and Author of the book 'Wings of Astra'",
    description: 'I am deeply moved by the mission of Himmatwaali. It is a powerful initiative that empowers women and promotes gender equality. I am proud to be a part of this movement and support the cause in any way I can.',
    video: personvedio2,
  },
  {
    name: 'Purnendu Shekhar',
    designation: 'Legendary Writer of Indian Television Serials',
    description: 'Having written milestones like Balika Vadhu, Astitva, Saat Phere and Veera.',
    video: personVedio,
  },
];

const DESKTOP_VISIBLE = 3;

function useVisibleSlideCount() {
  const [count, setCount] = useState(1);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const sync = () => setCount(media.matches ? DESKTOP_VISIBLE : 1);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return count;
}

const TestimonialCard = ({ entry }) => (
  <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md">
    <div className="flex min-h-[280px] items-center justify-center bg-[#f7f5f2] p-4 sm:min-h-[320px] lg:min-h-[360px]">
      <video
        src={entry.video}
        className="max-h-[min(520px,72vh)] w-full object-contain object-center"
        controls
        playsInline
        preload="metadata"
      />
    </div>
    <div className="flex flex-1 flex-col gap-2 p-5 md:p-6">
      <h3 className="text-lg font-semibold text-primary md:text-xl">
        {entry.name}
      </h3>
      
      {/* 2. Add the conditional rendering for the new property */}
      {entry.designation && (
        <p className="text-xs font-medium text-accent md:text-sm">
          {entry.designation}
        </p>
      )}

      {/* Added a top margin (mt-2) to separate the quote from the title */}
      <p className="mt-2 text-sm leading-relaxed text-gray-700 md:text-base">
        "{entry.description}"
      </p>
    </div>
  </article>
);


const AboutTestimonials = () => {
  const visibleCount = useVisibleSlideCount();
  const total = Testimonials.length;
  const maxIndex = Math.max(0, total - visibleCount);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(Math.max(0, Math.min(index, maxIndex)));
    },
    [maxIndex],
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  const useCarousel = total > visibleCount;
  const trackWidthPercent = (total / visibleCount) * 100;
  const slideShareOfTrack = 100 / total;

  const centeredSlotClass =
    total === 1
      ? 'w-full max-w-md'
      : total === 2
        ? 'w-full max-w-md lg:max-w-[calc((100%-2.5rem)/2)] lg:flex-[1_1_0]'
        : 'w-full max-w-md lg:max-w-[calc((100%-5rem)/3)] lg:flex-[1_1_0]';
  return (
    <section className="bg-bg-light px-6 py-14 md:px-12">
      <div className="mx-auto max-w-6xl">
        <h2 className="mb-10 text-center text-3xl font-bold uppercase text-accent md:mb-12 md:text-4xl">
          Our Testimonials
        </h2>

        {useCarousel ? (
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                width: `${trackWidthPercent}%`,
                transform: `translateX(-${activeIndex * slideShareOfTrack}%)`,
              }}
            >
              {Testimonials.map((entry) => (
                <div
                  key={entry.name}
                  className="box-border flex-shrink-0 px-2 sm:px-3"
                  style={{ width: `${slideShareOfTrack}%` }}
                >
                  <TestimonialCard entry={entry} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-stretch justify-center gap-6 lg:gap-10">
            {Testimonials.map((entry) => (
              <div key={entry.name} className={centeredSlotClass}>
                <TestimonialCard entry={entry} />
              </div>
            ))}
          </div>
        )}

        {useCarousel && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={goPrev}
              disabled={activeIndex === 0}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-white/80 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous participation"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>

            <div
              className="flex flex-wrap justify-center gap-2"
              role="tablist"
              aria-label="Participation slides"
            >
              {Array.from({ length: maxIndex + 1 }, (_, pageIndex) => (
                <button
                  key={pageIndex}
                  type="button"
                  role="tab"
                  aria-selected={pageIndex === activeIndex}
                  aria-label={`Go to slide group ${pageIndex + 1}`}
                  onClick={() => goTo(pageIndex)}
                  className={`h-2.5 rounded-full transition-all ${
                    pageIndex === activeIndex
                      ? 'w-8 bg-accent'
                      : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={activeIndex >= maxIndex}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-white/80 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next participation"
            >
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutTestimonials;
